import { useMemo } from 'react'
import { useCollection } from './useFirestore'
import { DEFAULT_SPELL_SCHOOLS } from '../lib/spellSchools'

/**
 * Hook to get spell schools from Firestore with fallback to defaults.
 *
 * @returns {{
 *   schools: Array,
 *   getSchool: (slug: string) => Object | null,
 *   getSchoolName: (slug: string) => string,
 *   getSchoolIcon: (slug: string) => string,
 *   loading: boolean
 * }}
 */
export function useSpellSchools() {
  const { data: firestoreSchools, loading } = useCollection('spellSchools', { orderByField: 'sortOrder' })

  const schools = useMemo(() => {
    // If Firestore schools are loaded and not empty, use them
    if (!loading && firestoreSchools.length > 0) {
      return firestoreSchools
    }
    // Otherwise, use defaults
    return DEFAULT_SPELL_SCHOOLS
  }, [firestoreSchools, loading])

  const schoolMap = useMemo(() => {
    const map = new Map()
    schools.forEach(school => {
      map.set(school.slug, school)
    })
    return map
  }, [schools])

  const getSchool = (slug) => {
    return schoolMap.get(slug) || null
  }

  const getSchoolName = (slug) => {
    const school = schoolMap.get(slug)
    return school?.name || slug
  }

  const getSchoolIcon = (slug) => {
    const school = schoolMap.get(slug)
    return school?.icon || '✨'
  }

  return {
    schools,
    getSchool,
    getSchoolName,
    getSchoolIcon,
    loading,
  }
}
